import { useState, useEffect, useCallback } from 'react';
import { useMatch, useNavigate } from '@tanstack/react-location';
import { useQuery } from 'react-query';
import { PaymentIntentStatus, getPaymentRequest } from '@frontend/api-weave-pay';
import { http, useFetch } from '@frontend/fetch';
import { RequestForPaymentType } from '../types/payment-request';
import { getUniqueLinkFromUrl } from '../utils';

export const notFoundPage = 'https://www.getweave.com/404';

export const usePaymentRequest = () => {
  const uniqueLink = getUniqueLinkFromUrl();
  const { setLocationIdHeader } = useFetch();
  const navigate = useNavigate();

  const { params } = useMatch();
  const { requestId } = params;

  const [windowLocationChange, setWindowLocationChange] = useState<boolean>(false);

  const navigateTo202 = () => {
    setWindowLocationChange(true);
    navigate({ to: `/202` });
  };

  const {
    data: paymentRequest,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [uniqueLink],
    queryFn: () => getPaymentRequest(requestId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    select: (data): RequestForPaymentType => ({
      ...data,
      personId: data.person.id,
      amount: data.amount / 100,
      self: data.links.self,
      payments: data.links.payments,
      attachment: data.links.attachment,
      locationLogo: data.links.locationLogo,
      personName: `${data.person.firstName} ${data.person.lastName}`,
      paymentId: data.paymentId,
      paymentMethods: data.links.paymentMethods,
      onBehalfOf: data.onBehalfOf,
      hasBirthDate: data.person.hasBirthDate,
    }),
  });

  const handlePaymentRequestData = useCallback(() => {
    if (!paymentRequest) return;
    setLocationIdHeader(paymentRequest.location?.id);
    if (paymentRequest.payment_intent?.status === PaymentIntentStatus.Succeeded) return navigate({ to: `/410` });
    else if (paymentRequest.links.processing) return navigateTo202();
  }, [paymentRequest]);

  const handlePaymentRequestError = useCallback(() => {
    if (!error) return;
    console.error(error);
    if (http.isHttpError(error) && [410, 202].includes(error.status)) {
      return navigate({ to: `/${error.status}` });
    }
    navigate({ to: `/error` });
  }, [error]);

  useEffect(() => {
    handlePaymentRequestData();
  }, [handlePaymentRequestData]);

  useEffect(() => {
    handlePaymentRequestError();
  }, [handlePaymentRequestError]);

  return { paymentRequest, loading: isLoading, windowLocationChange, refetchData: refetch };
};
